import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { changesApi } from '../../../services/changesApi';
import type { ChangeAgendaSendSummaryItem, ChangeAgendaQueuedSendSummaryItem, ChangeMeetingLite } from '../../../types/changes';
import { shortDispatchBadge } from '../agendaEmailDispatchStatus';
import CalendarAgendaEmailModal from './CalendarAgendaEmailModal';
import CalendarMeetingAgendaPanel from './CalendarMeetingAgendaPanel';

function formatClock(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const hours = Math.floor(safe / 3600);
  const minutes = String(Math.floor((safe % 3600) / 60)).padStart(2, '0');
  const seconds = String(safe % 60).padStart(2, '0');
  if (hours > 0) return `${hours}:${minutes}:${seconds}`;
  return `${minutes}:${seconds}`;
}

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  meeting: ChangeMeetingLite;
  canManage?: boolean;
  onClear: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSent?: (message: string) => void;
}

const CalendarSelectedMeetingSummary: React.FC<Props> = ({
  meeting,
  canManage = false,
  onClear,
  onEdit,
  onDelete,
  onSent,
}) => {
  const count = meeting.item_count || 0;
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [previewChangeId, setPreviewChangeId] = useState<number | undefined>();
  const [sentItems, setSentItems] = useState<ChangeAgendaSendSummaryItem[]>([]);
  const [queuedItems, setQueuedItems] = useState<ChangeAgendaQueuedSendSummaryItem[]>([]);

  const loadDispatchSummary = useCallback(async () => {
    if (!canManage) return;
    try {
      const response = await changesApi.meetingAgendaSendSummary(meeting.id);
      setSentItems(response.items || []);
      setQueuedItems(response.queued_items || []);
    } catch {
      setSentItems([]);
      setQueuedItems([]);
    }
  }, [canManage, meeting.id]);

  useEffect(() => {
    setEmailModalOpen(false);
    void loadDispatchSummary();
    let cancelled = false;
    void (async () => {
      try {
        const detail = await changesApi.getMeeting(meeting.id);
        if (cancelled) return;
        const first = detail.items?.[0]?.change_id;
        setPreviewChangeId(typeof first === 'number' ? first : undefined);
      } catch {
        if (!cancelled) setPreviewChangeId(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meeting.id, loadDispatchSummary]);

  useEffect(() => {
    if (emailModalOpen) void loadDispatchSummary();
  }, [emailModalOpen, loadDispatchSummary]);

  const dispatchBadge = shortDispatchBadge({ sentItems, queuedItems });

  return (
    <>
      <div className="border rounded px-3 py-2 mb-2" style={{ background: '#eef4ff', borderColor: '#0d6efd' }}>
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div>
            <div className="fw-semibold">
              Reunião · {meeting.title} · {meeting.status_label || meeting.status}
            </div>
            <div className="small text-muted">
              Clique no card destaca a reunião, como nas mudanças. Excluir apaga só a reunião; as
              mudanças da pauta continuam no sistema.
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Link to={`/changes/cab/${meeting.id}`} className="btn btn-sm btn-outline-primary">
              {meeting.status === 'encerrada' ? 'Ver tempos' : 'Abrir reunião'}
            </Link>
            {canManage ? (
              <>
                <Button size="sm" variant="outline-primary" onClick={() => setEmailModalOpen(true)}>
                  Enviar e-mail
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={onEdit}>
                  Editar
                </Button>
                <Button size="sm" variant="outline-danger" onClick={onDelete}>
                  Excluir
                </Button>
              </>
            ) : null}
            <Button size="sm" variant="outline-secondary" onClick={onClear}>
              Limpar
            </Button>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-1 mb-2">
          <Badge bg="dark" className="fw-normal">
            Reunião
          </Badge>
          <Badge bg="info" className="fw-normal">
            {meeting.kind_label}
          </Badge>
          {dispatchBadge ? (
            <Badge bg="secondary" className="fw-normal">
              {dispatchBadge}
            </Badge>
          ) : null}
          {queuedItems.length ? (
            <Badge bg="warning" text="dark" className="fw-normal">
              {queuedItems.length === 1 ? '1 disparo agendado' : `${queuedItems.length} disparos agendados`}
            </Badge>
          ) : null}
        </div>
        <div className="small">
          <div>Tipo: {meeting.kind_label}</div>
          <div>Horário: {formatWhen(meeting.scheduled_at)}</div>
          <div>Pauta: {count === 1 ? '1 mudança' : `${count} mudanças`}</div>
          <div>Tempo total: {formatClock(meeting.total_elapsed_seconds || 0)}</div>
        </div>
      </div>

      {canManage ? (
        <CalendarAgendaEmailModal
          show={emailModalOpen}
          onHide={() => setEmailModalOpen(false)}
          title="E-mail da reunião"
          subtitle={meeting.title}
        >
          <CalendarMeetingAgendaPanel
            embedded={false}
            meetingId={meeting.id}
            meetingTitle={meeting.title}
            meetingKind={meeting.kind}
            scheduledAt={meeting.scheduled_at}
            itemCount={count}
            previewChangeId={previewChangeId}
            sentItems={sentItems}
            queuedItems={queuedItems}
            onSent={(message) => {
              onSent?.(message);
              void loadDispatchSummary();
            }}
            onDispatchRefresh={() => void loadDispatchSummary()}
            onComplete={() => setEmailModalOpen(false)}
          />
        </CalendarAgendaEmailModal>
      ) : null}
    </>
  );
};

export default CalendarSelectedMeetingSummary;
